import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Shield, FileText, Users, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import AppLayout from '../layout/AppLayout';

const TermsOfService: React.FC = () => {
  const { t } = useTranslation();

  return (
    <AppLayout>
      <div className="flex-1 overflow-auto bg-main-bg min-h-full">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-3 mb-4">
                <FileText className="w-12 h-12 text-primary-500" />
                <h1 className="text-4xl font-bold text-gray-900">
                  {t('terms.title')}
                </h1>
              </div>
              <p className="text-gray-600 text-lg">
                {t('terms.lastUpdated')}: {new Date().toLocaleDateString()}
              </p>
            </div>

            <div className="prose prose-lg max-w-none text-gray-800">
              {/* Acceptance of Terms */}
              <section className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="w-6 h-6 text-primary-500" />
                  <h2 className="text-2xl font-semibold text-gray-900">
                    {t('terms.sections.acceptance.title')}
                  </h2>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  {t('terms.sections.acceptance.content')}
                </p>
              </section>

              {/* Service Description */}
              <section className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-6 h-6 text-primary-500" />
                  <h2 className="text-2xl font-semibold text-gray-900">
                    {t('terms.sections.service.title')}
                  </h2>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  {t('terms.sections.service.content')}
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>{t('terms.sections.service.features.vinLookup')}</li>
                  <li>{t('terms.sections.service.features.partSearch')}</li>
                  <li>{t('terms.sections.service.features.expertGuidance')}</li>
                  <li>{t('terms.sections.service.features.orderManagement')}</li>
                </ul>
              </section>

              {/* User Responsibilities */}
              <section className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <AlertCircle className="w-6 h-6 text-primary-500" />
                  <h2 className="text-2xl font-semibold text-gray-900">
                    {t('terms.sections.userResponsibilities.title')}
                  </h2>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  {t('terms.sections.userResponsibilities.content')}
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>{t('terms.sections.userResponsibilities.rules.accuracy')}</li>
                  <li>{t('terms.sections.userResponsibilities.rules.lawful')}</li>
                  <li>{t('terms.sections.userResponsibilities.rules.noAbuse')}</li>
                  <li>{t('terms.sections.userResponsibilities.rules.privacy')}</li>
                </ul>
              </section>

              {/* Privacy and Data */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  {t('terms.sections.privacy.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  {t('terms.sections.privacy.content')}
                </p>
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                  <p className="text-blue-800">
                    {t('terms.sections.privacy.dataUsage')}
                  </p>
                </div>
              </section>

              {/* Disclaimers */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  {t('terms.sections.disclaimers.title')}
                </h2>
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
                  <p className="text-yellow-800 mb-2">
                    <strong>{t('terms.sections.disclaimers.warning')}</strong>
                  </p>
                  <p className="text-yellow-800">
                    {t('terms.sections.disclaimers.content')}
                  </p>
                </div>
              </section>

              {/* Limitation of Liability */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  {t('terms.sections.liability.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('terms.sections.liability.content')}
                </p>
              </section>

              {/* Changes to Terms */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  {t('terms.sections.changes.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('terms.sections.changes.content')}
                </p>
              </section>

              {/* Contact Information */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  {t('terms.sections.contact.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('terms.sections.contact.content')}
                </p>
              </section>

              {/* Additional Links */}
              <section className="pt-8 border-t border-gray-200">
                <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
                  <Link
                    to="/privacy"
                    className="hover:text-primary-500 underline transition-colors"
                  >
                    {t('privacy.title')}
                  </Link>
                  <span>•</span>
                  <Link
                    to="/cookie-settings"
                    className="hover:text-primary-500 underline transition-colors"
                  >
                    {t('cookieSettings.title')}
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default TermsOfService;
